import React from 'react';
import './FormInput.css';

/**
 * FormInput
 * A reusable, accessible input field with icon support and error state.
 *
 * @param {object}      props
 * @param {string}      props.id
 * @param {string}      props.label
 * @param {string}      props.name
 * @param {string}      props.type
 * @param {string}      props.value
 * @param {Function}    props.onChange
 * @param {string}      [props.placeholder]
 * @param {string}      [props.error]
 * @param {boolean}     [props.disabled]
 * @param {string}      [props.autoComplete]
 * @param {boolean}     [props.autoFocus]
 * @param {React.ReactNode} [props.iconLeft]
 * @param {React.ReactNode} [props.iconRight]
 * @param {Function}    [props.onIconRightClick]
 * @param {string}      [props.iconRightLabel]
 */
export default function FormInput({
  id,
  label,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  error,
  disabled = false,
  autoComplete,
  autoFocus = false,
  iconLeft,
  iconRight,
  onIconRightClick,
  iconRightLabel,
}) {
  const errorId = error ? `${id}-error` : undefined;
  const hasError = Boolean(error);

  return (
    <div className="form-input-group">
      {label && (
        <label htmlFor={id} className="form-input__label">
          {label}
        </label>
      )}

      <div className="form-input__wrapper">
        {iconLeft && (
          <span className="form-input__icon-left" aria-hidden="true">
            {iconLeft}
          </span>
        )}

        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          aria-invalid={hasError}
          aria-describedby={errorId}
          className={[
            'form-input__field',
            iconLeft ? 'form-input__field--icon-left' : '',
            iconRight ? 'form-input__field--icon-right' : '',
            hasError ? 'form-input__field--error' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        />

        {iconRight && (
          <button
            type="button"
            className="form-input__icon-right"
            onClick={onIconRightClick}
            aria-label={iconRightLabel}
            tabIndex={-1}
          >
            {iconRight}
          </button>
        )}
      </div>

      {hasError && (
        <p id={errorId} className="form-input__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
